import React from "react";
import { Card, CardContent } from "@/components/ui/card";

/**
 * Compact stat tile. Used in summary strips at the top of admin pages.
 *  - icon: Lucide icon component
 *  - label: short label (e.g. "Deployed")
 *  - value: main number/string
 *  - hint: optional secondary text
 *  - accent: tailwind colour family — emerald | rose | amber | sky | violet | slate
 *  - testid: data-testid prefix
 */
const ACCENTS = {
  emerald: {
    chip: "bg-emerald-100 text-emerald-700",
    value: "text-emerald-700",
  },
  rose: {
    chip: "bg-rose-100 text-rose-700",
    value: "text-rose-700",
  },
  amber: {
    chip: "bg-amber-100 text-amber-700",
    value: "text-amber-700",
  },
  sky: {
    chip: "bg-sky-100 text-sky-700",
    value: "text-sky-700",
  },
  violet: {
    chip: "bg-violet-100 text-violet-700",
    value: "text-violet-700",
  },
  indigo: {
    chip: "bg-indigo-100 text-indigo-700",
    value: "text-indigo-700",
  },
  teal: {
    chip: "bg-teal-100 text-teal-700",
    value: "text-teal-700",
  },
  slate: {
    chip: "bg-slate-100 text-slate-700",
    value: "text-slate-700",
  },
};

export function StatTile({ icon: Icon, label, value, hint, accent = "slate", testid, onClick }) {
  const a = ACCENTS[accent] || ACCENTS.slate;
  const interactive = typeof onClick === "function";
  const baseCls = "min-w-0";
  const interactiveCls = interactive
    ? " cursor-pointer transition hover:shadow-md hover:ring-2 hover:ring-slate-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400"
    : "";
  return (
    <Card
      className={baseCls + interactiveCls}
      data-testid={testid}
      onClick={interactive ? onClick : undefined}
      role={interactive ? "button" : undefined}
      tabIndex={interactive ? 0 : undefined}
      onKeyDown={interactive ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onClick(); } } : undefined}
    >
      <CardContent className="flex items-center gap-3 p-3">
        {Icon && (
          <div className={`grid h-10 w-10 shrink-0 place-items-center rounded-lg ${a.chip}`}>
            <Icon className="h-5 w-5" strokeWidth={2} />
          </div>
        )}
        <div className="min-w-0">
          <div className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">{label}</div>
          <div className={`text-xl font-semibold tabular-nums ${a.value}`}>{value}</div>
          {hint && <div className="mt-0.5 text-[11px] text-muted-foreground truncate">{hint}</div>}
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * StatStrip — horizontal scroll-safe grid of StatTiles.
 */
export function StatStrip({ children, cols = 4 }) {
  const colCls = {
    2: "grid-cols-2",
    3: "grid-cols-2 sm:grid-cols-3",
    4: "grid-cols-2 sm:grid-cols-4",
    5: "grid-cols-2 sm:grid-cols-3 lg:grid-cols-5",
    6: "grid-cols-2 sm:grid-cols-3 lg:grid-cols-6",
  }[cols] || "grid-cols-2 sm:grid-cols-4";
  return <div className={`grid gap-2 ${colCls}`}>{children}</div>;
}
